import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Plus, TiktokLogo, InstagramLogo, YoutubeLogo, FacebookLogo, XLogo, LinkedinLogo,
  CalendarBlank, Clock, Sparkle, Trash, Eye, PaperPlaneTilt
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { format } from "date-fns";

const platforms = [
  { id: "tiktok", name: "TikTok", icon: TiktokLogo, color: "text-white" },
  { id: "instagram", name: "Instagram", icon: InstagramLogo, color: "text-pink-400" },
  { id: "youtube", name: "YouTube", icon: YoutubeLogo, color: "text-red-500" },
  { id: "facebook", name: "Facebook", icon: FacebookLogo, color: "text-blue-500" },
  { id: "twitter", name: "X", icon: XLogo, color: "text-white" },
  { id: "linkedin", name: "LinkedIn", icon: LinkedinLogo, color: "text-blue-400" },
];

const statusColors = {
  draft: "bg-zinc-500/20 text-zinc-400",
  scheduled: "bg-blue-500/20 text-blue-400",
  publishing: "bg-amber-500/20 text-amber-400",
  published: "bg-emerald-500/20 text-emerald-400",
  failed: "bg-rose-500/20 text-rose-400",
};

const PostCard = ({ post, onDelete }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className="bg-zinc-900/50 border border-white/5 rounded-2xl p-5 card-hover"
  >
    <div className="flex items-start justify-between mb-3">
      <span className={`status-badge ${statusColors[post.status]}`}>
        {post.status}
      </span>
      <button
        onClick={() => onDelete(post.id)}
        className="p-1.5 rounded-lg hover:bg-white/5 text-zinc-500 hover:text-rose-400 transition-colors"
      >
        <Trash size={16} />
      </button>
    </div>
    
    {/* Platforms */}
    <div className="flex gap-2 mb-3">
      {post.platforms.map(p => {
        const platform = platforms.find(pl => pl.id === p);
        if (!platform) return null;
        const Icon = platform.icon;
        return (
          <div key={p} className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
            <Icon size={18} className={platform.color} />
          </div>
        );
      })}
    </div>

    <p className="text-sm text-white mb-3 line-clamp-3">{post.caption || 'No caption'}</p>
    
    {post.hashtags?.length > 0 && (
      <div className="flex flex-wrap gap-1 mb-3">
        {post.hashtags.slice(0, 3).map((tag, i) => (
          <span key={i} className="text-xs text-cyan-400">#{tag}</span>
        ))}
        {post.hashtags.length > 3 && (
          <span className="text-xs text-zinc-500">+{post.hashtags.length - 3} more</span>
        )}
      </div>
    )}

    {post.scheduled_at && (
      <div className="flex items-center gap-2 text-xs text-zinc-500">
        <Clock size={14} />
        <span>{format(new Date(post.scheduled_at), 'MMM d, yyyy h:mm a')}</span>
      </div>
    )}
  </motion.div>
);

export default function SocialStudio() {
  const [posts, setPosts] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  // Form state
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [caption, setCaption] = useState("");
  const [hashtags, setHashtags] = useState("");
  const [ctaText, setCtaText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [scheduledDate, setScheduledDate] = useState(null);
  const [scheduledTime, setScheduledTime] = useState("");

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API}/social-posts`);
      setPosts(res.data);
    } catch (err) {
      toast.error("Failed to fetch posts");
    } finally {
      setLoading(false);
    }
  };

  const togglePlatform = (platformId) => {
    setSelectedPlatforms(prev =>
      prev.includes(platformId)
        ? prev.filter(p => p !== platformId)
        : [...prev, platformId]
    );
  };

  const handleCreate = async () => {
    if (selectedPlatforms.length === 0) {
      toast.error("Please select at least one platform");
      return;
    }

    let scheduledAt = null;
    if (scheduledDate && scheduledTime) {
      const [hours, minutes] = scheduledTime.split(':');
      const date = new Date(scheduledDate);
      date.setHours(parseInt(hours), parseInt(minutes));
      scheduledAt = date.toISOString();
    }

    try {
      const res = await axios.post(`${API}/social-posts`, {
        platforms: selectedPlatforms,
        caption,
        hashtags: hashtags.split(',').map(h => h.trim().replace('#', '')).filter(h => h),
        cta_text: ctaText,
        link_url: linkUrl,
        scheduled_at: scheduledAt,
      });
      setPosts([res.data, ...posts]);
      toast.success(scheduledAt ? "Post scheduled!" : "Post created as draft!");
      resetForm();
      setShowCreate(false);
    } catch (err) {
      toast.error("Failed to create post");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/social-posts/${id}`);
      setPosts(posts.filter(p => p.id !== id));
      toast.success("Post deleted");
    } catch (err) {
      toast.error("Failed to delete post");
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/social-posts/generate-content`, null, {
        params: { topic: "digital business and online entrepreneurship" }
      });
      
      // Try to parse as JSON, otherwise use raw
      try {
        const content = JSON.parse(res.data.generated_content);
        if (content.caption) setCaption(content.caption);
        if (content.hashtags) setHashtags(content.hashtags.join(', '));
        if (content.cta_text) setCtaText(content.cta_text);
      } catch {
        setCaption(res.data.generated_content);
      }
      
      toast.success("Content generated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate content");
    } finally {
      setGenerating(false);
    }
  };

  const resetForm = () => {
    setSelectedPlatforms([]);
    setCaption("");
    setHashtags("");
    setCtaText("");
    setLinkUrl("");
    setScheduledDate(null);
    setScheduledTime("");
  };

  // Group posts by status for calendar view
  const scheduledPosts = posts.filter(p => p.status === 'scheduled');
  const draftPosts = posts.filter(p => p.status === 'draft');
  const publishedPosts = posts.filter(p => p.status === 'published');

  return (
    <div data-testid="social-studio">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Social Media Studio
          </h1>
          <p className="text-zinc-400">Create and schedule posts across platforms</p>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          data-testid="create-post-btn"
          className="h-12 px-6 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold"
        >
          <Plus size={20} className="mr-2" />
          New Post
        </Button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-5">
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Drafts</p>
          <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {draftPosts.length}
          </p>
        </div>
        <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-5">
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Scheduled</p>
          <p className="text-3xl font-bold text-blue-400" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {scheduledPosts.length}
          </p>
        </div>
        <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-5">
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Published</p>
          <p className="text-3xl font-bold text-emerald-400" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {publishedPosts.length}
          </p>
        </div>
      </div>

      {/* Content Area */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : posts.length === 0 ? (
        <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-12 text-center">
          <CalendarBlank size={48} className="mx-auto mb-4 text-zinc-600" />
          <h3 className="text-xl font-semibold text-white mb-2">No posts yet</h3>
          <p className="text-zinc-400 mb-6">Create your first social media post</p>
          <Button
            onClick={() => setShowCreate(true)}
            className="h-12 px-6 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold"
          >
            Create Post
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {posts.map((post) => (
            <PostCard key={post.id} post={post} onDelete={handleDelete} />
          ))}
        </div>
      )}

      {/* Create Post Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Create Social Post
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 mt-4">
            {/* Platform Selection */}
            <div className="space-y-2">
              <Label className="text-zinc-400">Select Platforms *</Label>
              <div className="flex flex-wrap gap-3">
                {platforms.map(platform => {
                  const Icon = platform.icon;
                  const isSelected = selectedPlatforms.includes(platform.id);
                  return (
                    <button
                      key={platform.id}
                      onClick={() => togglePlatform(platform.id)}
                      data-testid={`platform-${platform.id}`}
                      className={`platform-icon ${isSelected ? 'selected' : ''}`}
                    >
                      <Icon size={22} className={platform.color} weight={isSelected ? "fill" : "regular"} />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* AI Generate Button */}
            <Button
              onClick={handleGenerate}
              disabled={generating}
              data-testid="generate-social-content-btn"
              className="w-full h-12 rounded-xl bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 text-black font-semibold"
            >
              {generating ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Generating...
                </span>
              ) : (
                <>
                  <Sparkle size={20} className="mr-2" weight="fill" />
                  Generate with AI
                </>
              )}
            </Button>

            {/* Caption */}
            <div className="space-y-2">
              <Label className="text-zinc-400">Caption</Label>
              <Textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Write your post caption..."
                data-testid="post-caption-input"
                className="bg-zinc-950 border-white/10 rounded-xl min-h-[120px]"
              />
            </div>

            {/* Hashtags */}
            <div className="space-y-2">
              <Label className="text-zinc-400">Hashtags (comma separated)</Label>
              <Input
                value={hashtags}
                onChange={(e) => setHashtags(e.target.value)}
                placeholder="business, entrepreneur, academy"
                className="h-12 bg-zinc-950 border-white/10 rounded-xl"
              />
            </div>

            {/* CTA & Link */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Call to Action</Label>
                <Input
                  value={ctaText}
                  onChange={(e) => setCtaText(e.target.value)}
                  placeholder="Join the academy!"
                  className="h-12 bg-zinc-950 border-white/10 rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Link URL</Label>
                <Input
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://..."
                  className="h-12 bg-zinc-950 border-white/10 rounded-xl"
                />
              </div>
            </div>

            {/* Schedule */}
            <div className="space-y-2">
              <Label className="text-zinc-400">Schedule (Optional)</Label>
              <div className="grid grid-cols-2 gap-4">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="h-12 justify-start text-left font-normal bg-zinc-950 border-white/10 rounded-xl hover:bg-zinc-800"
                    >
                      <CalendarBlank className="mr-2 h-4 w-4 text-zinc-400" />
                      {scheduledDate ? format(scheduledDate, 'MMM d, yyyy') : <span className="text-zinc-500">Pick date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-zinc-900 border-white/10">
                    <Calendar
                      mode="single"
                      selected={scheduledDate}
                      onSelect={setScheduledDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <Input
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  className="h-12 bg-zinc-950 border-white/10 rounded-xl"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={() => setShowCreate(false)}
                className="flex-1 h-12 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                data-testid="save-post-btn"
                className="flex-1 h-12 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold"
              >
                {scheduledDate ? 'Schedule Post' : 'Save as Draft'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
